/*
 * DarkTimeWarningAlert — the shared surface for "this window runs past dark time" (#1119).
 *
 * Used in two places, deliberately identical so the message reads the same before and after save:
 *   - inline in the observation form, fed by useDarkTimeWarning (live, pre-submit);
 *   - after a create/update, fed by warningMessages(response) (the server's advisory).
 *
 * `severity="warning"`, never "error": the window is legal and the observation is created. What is
 * being reported is that the dispatcher will stop the run at dark time, so the tail of the window
 * will not be observed.
 *
 * `onContinue` renders an acknowledge action. Pages that auto-navigate on success pass it and hold
 * navigation until the user clicks — a warning that flashes for a second on its way to another
 * screen is not a warning anyone reads.
 */

import React from 'react';
import { Alert, AlertTitle, Box, Button } from '@mui/material';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';

export default function DarkTimeWarningAlert({
  messages = [],
  onContinue = null,
  continueLabel = 'Continue',
  sx = {},
}) {
  const list = (Array.isArray(messages) ? messages : [messages]).filter(Boolean);
  if (list.length === 0) return null;

  return (
    <Alert severity="warning" icon={<WbTwilightIcon fontSize="inherit" />} sx={{ mb: 2, ...sx }}>
      <AlertTitle>Window runs past dark time</AlertTitle>
      {list.map((msg, i) => (
        <Box key={i} sx={{ mb: i < list.length - 1 ? 0.5 : 0 }}>{msg}</Box>
      ))}
      {onContinue && (
        <Box sx={{ mt: 1.5 }}>
          <Button size="small" variant="outlined" color="warning" onClick={onContinue}>
            {continueLabel}
          </Button>
        </Box>
      )}
    </Alert>
  );
}
